import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signup, verifyPhoneNumber, sendPhoneVerificationCode } from '../../api/auth';
import { AuthContext } from '../../context/AuthContext';
import InputField from '../common/InputField';
import { Button } from '../common/Button';
import { AiOutlineEyeInvisible, AiOutlineEye, AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { FaUser, FaBriefcase } from 'react-icons/fa';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import axios from 'axios';
import LoadingSpinner from '../common/Loader';
import { v4 as uuidv4 } from 'uuid';
import Modal from '../common/Modal';

const SignupForm = () => {
  const navigate = useNavigate();
  const { authlogin } = useContext(AuthContext);

  const Onboarding_account_managerId = "c94d0400";
  const [formData, setFormData] = useState({
    id: uuidv4(),
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    phoneNumber: '',
    account_type: 'Real',
    role: 'User',
    is_verified: false,
    is_approved: false,
    platform: 'web',
    terms1: false,
    terms2: false,
    referCode: '',
    account_manager_id: Onboarding_account_managerId,
    account_nature: '',
  });

  const [showOrgMessage, setShowOrgMessage] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const passwordTooltipRef = useRef(null);

  // Phone verification states
  const [otp, setOtp] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showOtpModal, setShowOtpModal] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const referralCodeEncoded = queryParams.get('ref');

    if (referralCodeEncoded) {
      try {
        const referralCodeDecoded = atob(referralCodeEncoded);
        const referralParts = referralCodeDecoded.split(':');

        if (referralParts.length === 2) {
          const [type, referrCode] = referralParts;
          setFormData(prev => ({ ...prev, referCode: referrCode }));
        }
      } catch (error) {
        console.error('Error decoding referral code:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchCountryCode = async () => {
      try {
        const response = await axios.get('https://ipapi.co/country/');
        setCountryCode(response.data);
      } catch (error) {
        console.error('Failed to detect country code:', error);
      }
    };

    fetchCountryCode();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    // Close tooltip when clicking outside
    const handleClickOutside = (event) => {
      if (
        passwordTooltipRef.current && 
        !passwordTooltipRef.current.contains(event.target) &&
        !event.target.closest('.password-input-field')
      ) {
        setShowPasswordTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors(prev => ({ ...prev, [name]: '' }));

    if (name === 'password') {
      validatePasswordStrength(value);
    }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phoneNumber: value }));
    setErrors(prev => ({ ...prev, phoneNumber: '' }));
    setIsPhoneVerified(false);
    setOtpSent(false);
  };

  const handleVerifyClick = async () => {
    if (!formData.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: 'Phone number is required' }));
      return;
    }

    setIsSendingOtp(true);
    setOtpError('');

    try {
      const recaptchaToken = await executeRecaptcha('requestOtp');

      if (!recaptchaToken) {
        setOtpError('Failed to verify you are human. Please try again.');
        return;
      }

      await sendPhoneVerificationCode(formData.phoneNumber, recaptchaToken);
      setOtpSent(true);
      setCountdown(60);
      setShowOtpModal(true);
    } catch (error) {
      setOtpError(error.message || 'Failed to send verification code');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const isPhoneNumberValid = () => {
    return formData.phoneNumber && formData.phoneNumber.replace(/\D/g, '').length > 5;
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setOtpError('Verification code is required');
      return;
    }

    if (otp.length < 6) {
      setOtpError('Verification code must be 6 digits');
      return;
    }

    setLoading(true);
    setOtpError('');

    try {
      const recaptchaToken = await executeRecaptcha('verifyOtp');

      if (!recaptchaToken) {
        setOtpError('Failed to verify you are human. Please try again.');
        return;
      }

      await verifyPhoneNumber(formData.phoneNumber, otp, recaptchaToken);
      setIsPhoneVerified(true);
      setOtp('');
      setOtpSent(false);
      setShowOtpModal(false);
    } catch (error) {
      setOtpError(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;

    setIsSendingOtp(true);
    setOtpError('');

    try {
      const recaptchaToken = await executeRecaptcha('requestOtp');

      if (!recaptchaToken) {
        setOtpError('Failed to verify you are human. Please try again.');
        return;
      }

      await sendPhoneVerificationCode(formData.phoneNumber, recaptchaToken);
      setCountdown(60);
    } catch (error) {
      setOtpError(error.message || 'Failed to resend verification code');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const continueWithIndividual = () => {
    setFormData(prev => ({ ...prev, account_nature: 'Individual' }));
  };
  const buildCard = (type, IconComponent) => {
  const isSelected = formData.account_nature === (type === 'Company' ? 'Organizational' : 'Individual');
  const isCompany = type === 'Company';

  return (
    <div
      onClick={() => {
        if (isCompany) {
          setFormData(prev => ({ ...prev, account_nature: 'Organizational' }));
        } else {
          setFormData(prev => ({ ...prev, account_nature: 'Individual' }));
        }
      }}
      className={`cursor-pointer p-2 rounded-md border-2 flex flex-col items-center transition-all duration-200 ${
        isSelected
          ? 'border-red-500 bg-white' // Red border + white background when selected
          : 'border-gray-300 bg-white hover:border-primary hover:bg-primary/10 hover:text-primary'
      }`}
      style={{
        minHeight: '80px',
        width: '30%',
        flex: 'none'
      }}
    >
      <IconComponent 
        size={20} 
        className={`mb-1 ${isSelected ? 'text-red-500' : 'text-gray-700'}`} // Red icon when selected
      />
      <span className={`text-sm font-medium ${isSelected ? 'text-red-500' : 'text-gray-700'}`}>
        {type}
      </span>
    </div>
  );
};
  const validatePasswordStrength = (password) => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const isStrong = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
    setPasswordStrength(isStrong ? 'strong' : 'weak');
  };

  const executeRecaptcha = async (action = 'signup') => {
    try {
      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA not loaded');
      }

      return new Promise((resolve) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action })
            .then(resolve)
            .catch((error) => {
              console.error(`reCAPTCHA execution failed for action "${action}":`, error);
              resolve(null);
            });
        });
      });
    } catch (error) {
      console.error(`reCAPTCHA execution failed for action "${action}":`, error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.account_nature) {
      setErrors(prev => ({ ...prev, general: 'Please select account type (Individual or Company)' }));
      return;
    }

    // if (!isPhoneVerified) {
    //   setErrors(prev => ({ ...prev, phoneNumber: 'Please verify your phone number' }));
    //   return;
    // }

    setLoading(true);

    if (!formData.terms1 || !formData.terms2) {
      setErrors(prev => ({ ...prev, general: 'You must accept both terms and conditions.' }));
      setLoading(false);
      return;
    }

    if (passwordStrength === 'weak') {
      setErrors(prev => ({
        ...prev,
        general: 'Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.',
      }));
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors(prev => ({ ...prev, general: 'Passwords do not match.' }));
      setLoading(false);
      return;
    }

    try {
      const token = await executeRecaptcha();
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await signup({
        ...formData,
        recaptchaToken: token
      });

      if (!response.error) {
        authlogin(
          response.access_token,
          response.user_id,
          response.refresh_token,
          response.kyc_completed,
          formData.email,
          response.is_verified,
          formData.account_nature,
          formData.role,
          response.current_step || 1
        );
        navigate('/email-verification');
      } else {
        setErrors(prev => ({ ...prev, general: response.message }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, general: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const PasswordTooltip = () => (
    <div 
      ref={passwordTooltipRef}
      className={`password-tooltip absolute z-[1000] w-full max-w-[280px] top-[calc(100%+5px)] left-0 bg-white border border-gray-300 rounded-md shadow-lg p-3 transition-all duration-200 ${
        showPasswordTooltip ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}
    >
      <h4 className="font-medium text-sm mb-2 text-gray-800">Password Requirements:</h4>
      <ul className="text-xs text-gray-600 space-y-1">
        <li className={`flex items-center ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-500'}`}>
          {formData.password.length >= 8 ? (
            <AiOutlineCheck className="mr-1" size={14} />
          ) : (
            <AiOutlineClose className="mr-1" size={14} />
          )}
          At least 8 characters
        </li>
        <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-500'}`}>
          {/[A-Z]/.test(formData.password) ? (
            <AiOutlineCheck className="mr-1" size={14} />
          ) : (
            <AiOutlineClose className="mr-1" size={14} />
          )}
          At least one uppercase letter
        </li>
        <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-500' : 'text-gray-500'}`}>
          {/[a-z]/.test(formData.password) ? (
            <AiOutlineCheck className="mr-1" size={14} />
          ) : (
            <AiOutlineClose className="mr-1" size={14} />
          )}
          At least one lowercase letter
        </li>
        <li className={`flex items-center ${/\d/.test(formData.password) ? 'text-green-500' : 'text-gray-500'}`}>
          {/\d/.test(formData.password) ? (
            <AiOutlineCheck className="mr-1" size={14} />
          ) : (
            <AiOutlineClose className="mr-1" size={14} />
          )}
          At least one number
        </li>
        <li className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-green-500' : 'text-gray-500'}`}>
          {/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? (
            <AiOutlineCheck className="mr-1" size={14} />
          ) : (
            <AiOutlineClose className="mr-1" size={14} />
          )}
          At least one special character
        </li>
      </ul>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md w-full max-w-2xl my-8">
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Start your Application</h2>
          {errors.general && <p className="text-primary text-sm mb-4 text-center">{errors.general}</p>}

          {showOrgMessage && (
            <div className="mb-4 p-3 border border-red-200 rounded-lg bg-red-50">
              <h3 className="text-lg font-bold text-red-600 mb-1">Corporate Account Notice</h3>
              <p className="text-gray-700 mb-2 text-sm">
                Corporate accounts are not currently available. Please continue with an individual account.
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={continueWithIndividual}
                  className="text-red-600 text-sm font-medium hover:underline"
                >
                  Continue with Individual Account
                </button>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-center text-gray-700 mb-2 text-sm font-bold">Select Account Type</h3>
            <div className="flex justify-center gap-4 mb-6">
              {buildCard('Individual', FaUser)}
              {buildCard('Company', FaBriefcase)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <InputField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              error={errors.email}
            />
            <InputField
              label="Username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username"
              required
              error={errors.username}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-1">Phone Number</label>
            <div className="relative w-full">
              <PhoneInput
                international
                defaultCountry={countryCode}
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Enter your phone number"
                disabled={isPhoneVerified}
                className={`w-full pr-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors.phoneNumber ? 'border-primary' : 'border-gray-300'
                  } ${isPhoneVerified ? 'bg-gray-100 text-green-500' : ''}`}
              />

              {!isPhoneVerified && (
                <button
                  type="button"
                  onClick={handleVerifyClick}
                  disabled={!isPhoneNumberValid() || isSendingOtp}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium ${isPhoneNumberValid() ? 'text-primary hover:underline' : 'text-gray-400 cursor-not-allowed'
                    } bg-white px-2 py-1 rounded`}
                >
                  {isSendingOtp ? 'Sending...' : 'Verify'}
                </button>
              )}

              {isPhoneVerified && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center text-green-500">
                  <AiOutlineCheck size={20} />
                </div>
              )}
            </div>
            {errors.phoneNumber && <p className="text-primary text-xs mt-1">{errors.phoneNumber}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="relative password-tooltip-container">
              <InputField
                label="Password"
                type={passwordVisible ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setShowPasswordTooltip(true)}
                placeholder="Enter your password"
                required
                error={errors.password}
                icon={passwordVisible ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
                onIconClick={() => setPasswordVisible(!passwordVisible)}
                className="password-input-field"
              />
              <PasswordTooltip />
            </div>

            <InputField
              label="Confirm Password"
              type={confirmPasswordVisible ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
              error={errors.confirmPassword}
              icon={confirmPasswordVisible ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
              onIconClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
            />
          </div>

          <div className="mb-4 mt-4">
            <label className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="terms1"
                checked={formData.terms1}
                onChange={handleChange}
                className="form-checkbox h-4 w-4 text-primary focus:ring-primary mt-0.5"
              />
              <span className="text-gray-700 text-xs leading-tight">
                I accept the
                <a
                  href="https://investain.com/wp-content/uploads/2024/09/Terms-Conditions.pdf"
                  className="text-primary hover:underline ml-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </a>{' '}
                and
                <a
                  href="https://investain.com/wp-content/uploads/2024/09/Risk-Disclosure.pdf"
                  className="text-primary hover:underline ml-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Risk Disclosure
                </a>
                .
              </span>
            </label>
          </div>

          <div className="mb-6">
            <label className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="terms2"
                checked={formData.terms2}
                onChange={handleChange}
                className="form-checkbox h-4 w-4 text-primary focus:ring-primary mt-0.5"
              />
              <span className="text-gray-700 text-xs leading-tight">
                I acknowledge that my information will be used in accordance with the
                <a
                  href="https://investain.com/privacy-policy"
                  className="text-primary hover:underline ml-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and
                <a
                  href="https://investain.com/wp-content/uploads/2024/09/cookies_policy.pdf"
                  className="text-primary hover:underline ml-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie Policy
                </a>
                .
              </span>
            </label>
          </div>

          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={loading}
              className="w-full max-w-md"
              data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              data-callback="onSubmit"
              data-action="submit"
            >
              {loading ? 'Signing Up...' : 'Sign Up'}
            </Button>
          </div>

          {showOtpModal && (
            <Modal isOpen={showOtpModal} onClose={() => setShowOtpModal(false)}>
              <div className="bg-white p-4 rounded-lg max-w-sm w-full">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-bold text-gray-800">Verify Phone Number</h3>
                  <button
                    type="button"
                    onClick={() => setShowOtpModal(false)}
                    className="inline-flex items-center justify-center bg-transparent border-primary text-primary font-bold py-2 px-3 rounded-lg border transition-colors duration-200 hover:bg-primary hover:text-white hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <AiOutlineClose size={18} />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-3">
                  We've sent a 6-digit verification code to {formData.phoneNumber}
                </p>

                <div className="mb-3">
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={6}
                  />
                  {otpError && <p className="text-primary text-xs mt-1">{otpError}</p>}
                </div>

                <div className="flex justify-between items-center mb-4">
                  <p className="text-xs text-gray-600">
                    {countdown > 0 ? (
                      `Resend code in ${countdown}s`
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        className="bg-transparent border-primary text-primary font-bold py-1 px-3 rounded-lg border transition-colors duration-200 hover:bg-primary hover:text-white hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                      >
                        Resend Code
                      </button>
                    )}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={!otp || otp.length < 6 || loading}
                  className="w-full bg-transparent border-primary text-primary font-bold py-2 px-4 rounded-lg border transition-colors duration-200 hover:bg-primary hover:text-white hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify Phone Number'}
                </button>
              </div>
            </Modal>
          )}
        </form>
      )}
    </div>
  );
};

export default SignupForm;